/**
 * PENDÊNCIAS: o que está esperando alguém, em grupos.
 *
 * Responde "me traz um resumo de pendências" e alimenta o lote ("emitir
 * todas as entradas pendentes"). Nenhuma IA aqui: são as regras que as telas
 * já usam — a entrada a emitir é `entradaAEmitir`, o pronto para faturar é o
 * mesmo da cadeia de próximo passo, a etapa atrasada é a pendente com a data
 * agendada no passado.
 *
 * Cada grupo diz qual fluxo resolve os itens dele (`alvo`): é o que a fila
 * abre, um serviço de cada vez.
 */
import { SCHEDULE_BY_ID, etapaPendente } from '../../constants/schedule';
import { entradaAEmitir, etapaQueFecha } from '../calculations/entrada';

const hojeLocal = () => {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};
const dia = (v) => (v ? String(v).slice(0, 10) : null);
const reais = (n) => `R$ ${Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dataCurta = (iso) => { const [, m, d] = String(iso).split('-'); return `${d}/${m}`; };

/** Pronto para faturar: a etapa que fecha a conta foi feita e há valor. */
export const prontoParaFaturar = (s, ctx = {}) => {
  const fecha = etapaQueFecha(s);
  return !s.billed && Boolean(fecha && s[SCHEDULE_BY_ID[fecha]?.doneField])
    && (ctx.valorDoServico?.(s) || 0) > 0;
};

/** A entrada a emitir agora, ou nada. Sem saber o que já saiu, não oferece. */
const entradaDe = (s, ctx) => {
  if (ctx.entradaEmitidaPorServico === null) return null;
  const r = entradaAEmitir(s, ctx.lab, Boolean(ctx.entradaEmitidaPorServico?.[s.id]));
  return r.deve ? r : null;
};

/**
 * Os grupos, na ordem em que se resolvem: dinheiro a pedir primeiro, depois
 * o que está atrasado no campo. Grupo vazio não aparece.
 */
export const pendencias = (ctx = {}, hoje = hojeLocal()) => {
  const services = ctx.services || [];
  const grupos = [];
  const juntar = (g) => { if (g.itens.length) grupos.push(g); };

  const entradas = services.map(s => ({ s, e: entradaDe(s, ctx) })).filter(x => x.e);
  juntar({
    id: 'entradas', titulo: 'Entradas a emitir', alvo: 'emitir_entrada',
    valor: entradas.reduce((t, x) => t + x.e.valor, 0),
    itens: entradas.map(({ s, e }) => ({ s, valor: e.valor, detalhe: `${e.quantidade} embriões · ${reais(e.valor)}` })),
  });

  const vencidas = services.map(s => {
    const abertas = (ctx.parcelasPorServico?.[s.id] || [])
      .filter(p => !p.paga_em && dia(p.vencimento) && dia(p.vencimento) < hoje);
    return { s, abertas };
  }).filter(x => x.abertas.length);
  juntar({
    id: 'vencidas', titulo: 'Parcelas vencidas', alvo: 'receber',
    valor: vencidas.reduce((t, x) => t + x.abertas.reduce((u, p) => u + (Number(p.valor) || 0), 0), 0),
    itens: vencidas.map(({ s, abertas }) => ({
      s, valor: abertas.reduce((u, p) => u + (Number(p.valor) || 0), 0), detalhe: `${abertas.length} parcela${abertas.length > 1 ? 's' : ''} · ${reais(abertas.reduce((u, p) => u + (Number(p.valor) || 0), 0))}`,
    })),
  });

  const faturar = services.filter(s => prontoParaFaturar(s, ctx));
  juntar({
    id: 'faturar', titulo: 'Prontos para faturar', alvo: 'faturar',
    valor: faturar.reduce((t, s) => t + (ctx.valorDoServico?.(s) || 0), 0),
    itens: faturar.map(s => ({ s, valor: ctx.valorDoServico?.(s) || 0, detalhe: reais(ctx.valorDoServico?.(s)) })),
  });

  // Etapa atrasada: a PRÓXIMA pendente do serviço, com a data agendada já
  // passada. Uma por serviço — a de depois ainda nem tem como acontecer.
  const porEtapa = new Map();
  services.forEach(s => {
    const e = etapaPendente(s);
    const agendada = e && dia(s[e.field]);
    if (!agendada || agendada >= hoje) return;
    if (!porEtapa.has(e.id)) porEtapa.set(e.id, { e, itens: [] });
    porEtapa.get(e.id).itens.push({ s, detalhe: `era ${dataCurta(agendada)}`, agendada });
  });
  [...porEtapa.values()].forEach(({ e, itens }) => juntar({
    id: `etapa-${e.id}`, titulo: `${e.label} com data passada`, alvo: 'abrir_etapa', etapa: e.id,
    itens: itens.sort((a, b) => a.agendada.localeCompare(b.agendada)),
  }));

  return grupos;
};

/** O grupo que o lote de um fluxo percorre ("emitir todas as entradas"). */
export const grupoDoLote = (fluxo, ctx = {}, { etapa = null } = {}, hoje = hojeLocal()) => {
  const todos = pendencias(ctx, hoje);
  if (fluxo === 'abrir_etapa') {
    const g = todos.filter(x => x.alvo === 'abrir_etapa' && (!etapa || x.etapa === etapa));
    if (g.length <= 1) return g[0] || null;
    // Sem etapa dita: as atrasadas de todas, cada uma na sua.
    return { id: 'etapas', titulo: 'Etapas com data passada', alvo: 'abrir_etapa',
      itens: g.flatMap(x => x.itens.map(i => ({ ...i, etapa: x.etapa }))) };
  }
  return todos.find(x => x.alvo === fluxo) || null;
};

export { reais };
